// ============================================================================
// LEDGER MAPPING HOOKS (Fairbooks integration configuration)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { ledgerMappingService } from 'api/services/ledgerMappingService';
import type {
  AccountingLedgerMap,
  AccountingLedgerMapSearchRequest,
  UpsertAccountingLedgerMapRequest,
  LedgerMap,
  UpsertLedgerMapRequest,
  MappingLookups,
  ID,
} from 'api/types';

export const useLedgerMappings = (params?: AccountingLedgerMapSearchRequest) => {
  const [data, setData] = useState<AccountingLedgerMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerMappingService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, pagination, refetch: fetchAll };
};

export const useMappingLookups = () => {
  const [data, setData] = useState<MappingLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerMappingService.getLookups();
      setData(response.data);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export const useTransactionMaps = () => {
  const [data, setData] = useState<LedgerMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerMappingService.getTransactionMaps();
      setData(response.data ?? []);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export const useCreateLedgerMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createLedgerMapping = async (
    data: UpsertAccountingLedgerMapRequest,
    options?: { onSuccess?: (item: AccountingLedgerMap) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerMappingService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createLedgerMapping, loading, error };
};

export const useUpdateLedgerMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateLedgerMapping = async (
    id: ID,
    data: UpsertAccountingLedgerMapRequest,
    options?: { onSuccess?: (item: AccountingLedgerMap) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerMappingService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateLedgerMapping, loading, error };
};

export const useDeleteLedgerMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteLedgerMapping = async (
    id: ID,
    rowVersion: number,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await ledgerMappingService.delete(id, rowVersion);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteLedgerMapping, loading, error };
};

export const useUpsertTransactionMap = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const upsertTransactionMap = async (
    data: UpsertLedgerMapRequest,
    options?: { onSuccess?: (item: LedgerMap) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerMappingService.upsertTransactionMap(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { upsertTransactionMap, loading, error };
};
